import { Customer } from "@mcleod/ar";
import { Location } from "@mcleod/dispatch";
import { Prospect } from "@mcleod/crm";
import { Api, ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { ModelCity, RowCity } from "@mcleod/common";
import { DataSourceMode, LookupModelSelectionEvent, Snackbar } from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { LMEGeneralSettings, TableContact } from "@mcleod/general";

export interface GoogleSearchDecoratorProps {
    cityField?: string;
    cityIdField?: string;
    stateField?: string;
    zipField?: string;
}

export class GoogleSearchDecorator {
    private readonly layout: Customer | Location | Prospect;
    private searchProps: GoogleSearchDecoratorProps;
    private modelCity: ModelCity = new ModelCity();

    constructor(layout: Customer | Location | Prospect, props: GoogleSearchDecoratorProps) {
        this.layout = layout;
        this.layout.textboxGooglePlacesSearch.visible = false;
        this.layout.textboxGooglePlacesSearch.lookupModelInputDelay = LMEGeneralSettings.getSingleton().getDispatchControl().google_places_entry_delay;
        this.layout.textboxGooglePlacesSearch.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.textboxGooglePlacesSearchOnLookupModelSelection(event));
        this.layout.mainDataSource.addAfterModeChangeListener(() => this.afterModeChange());
        this.searchProps = props;
    }

    async textboxGooglePlacesSearchOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const data = event.selection;
        if (data != null) {
            if (data.get("location_id") != null) {
                this.createGoogleSearchMessagePanel(data);
            } else {
                await this.createLocation(data);
                if (this.layout instanceof Location) {
                    this.layout.sourceLocation.displayDataInBoundComponent(this.layout.mapLocation);
                    if (this.layout.mapLocation.map != null) {
                        this.layout.mapLocation.fitPins();
                        this.layout.mapLocation.setZoom(14);
                    }
                }
            }
        }
    }

    afterModeChange() {
        const dispatchControl = LMEGeneralSettings.getSingleton().getDispatchControl();
        this.layout.textboxGooglePlacesSearch.visible =
            (this.layout.mainDataSource.mode == DataSourceMode.ADD &&
            dispatchControl.enable_google_places == "Y" &&
            (this.layout instanceof Location ? dispatchControl.enable_location_add_google == "Y" :
                dispatchControl.enable_customer_add_google == "Y"));
    }

    async createLocation(googlePlacesResult: ModelRow) {
        const row = this.layout.mainDataSource.activeRow;
        if (googlePlacesResult.get("type") != "locality") {
            const name = googlePlacesResult.get("name");
            const address1 = googlePlacesResult.get("address1");
            this.layout.textboxName.text = name;
            this.layout.textboxAddress1.text = address1;
            row.set("name", name);
            row.set("address1", address1);
        }
        row.set("google_place_id", googlePlacesResult.get("google_place_id"));
        row.set("latitude", googlePlacesResult.get("latitude"));
        row.set("longitude", googlePlacesResult.get("longitude"));
        row.set("is_geocoded", true);

        // Get data from Google Places Details API for the selected place
        const googlePlaceId = row.get("google_place_id");
        let googlePlacesDetailResult;
        if (googlePlaceId != null) {
            await Api.search("dispatch/google-places-detail", { "google_place_id": googlePlaceId }).then(response => {
                for (const data of response.data) {
                    this.layout.textboxWebsiteUrl.text = data["website"];
                    this.addPhoneContact(data["phone_number"])
                    googlePlacesDetailResult = response.data[0];
                }
            });
        }

        await this.populateCityDetails(googlePlacesResult, googlePlacesDetailResult, row);

        this.layout.textboxGooglePlacesSearch.visible = false;
    }

    async populateCityDetails(googlePlacesResult: ModelRow, googlePlacesDetailResult: any, row: ModelRow) {
        if (StringUtil.isEmptyString(googlePlacesResult.get("city_id"))) {
            if (googlePlacesDetailResult) {
                const cityName = googlePlacesDetailResult.city ?? "";
                const zipCode = googlePlacesDetailResult.zip ?? "";
                const country = googlePlacesDetailResult.country ?? "";

                const modelCity = new ModelCity();
                let rowCity: RowCity | null = await modelCity.searchSingle({
                    // Remove accents from city name for search, it would be nice if we
                    // can add this logic to the backend so it can search with and without accents
                    name: this.removeAccents(cityName),
                    zip_code: this.removeAccents(zipCode),
                    zone_id: this.removeAccents(country)
                });

                if (!rowCity) {
                    // Fallback to city + country
                    rowCity = await this.modelCity.searchSingle({
                        name: this.removeAccents(cityName),
                        zone_id: this.removeAccents(country)
                    });
                }

                if (rowCity) {
                    row.set(this.searchProps.cityField, rowCity.get("name"));
                    row.set(this.searchProps.stateField, rowCity.get("state_id"));
                    row.set(this.searchProps.zipField, rowCity.get("zip_code"));
                    row.set(this.searchProps.cityIdField, rowCity.get("id"));
                } else {
                    this.showCityNotFoundWarning();
                }

            } else {
                this.showGooglePlacesDetailError();
            }
        } else {
            row.set(this.searchProps.cityField, googlePlacesResult.get("city_name"));
            row.set(this.searchProps.stateField, googlePlacesResult.get("state"));
            row.set(this.searchProps.zipField, googlePlacesResult.get("zip_code"));
            row.set(this.searchProps.cityIdField, googlePlacesResult.get("city_id"));
        }
    }

    private showCityNotFoundWarning() {
        Snackbar.showWarningSnackbar("No city found for this location. Please enter the city manually.", { targetPanel: this.layout });
    }

    private showGooglePlacesDetailError() {
        Snackbar.showWarningSnackbar("Could not retrieve Google Places details. Please enter the city manually.", { targetPanel: this.layout });
    }

    // Removes accents/diacritics from a string
    removeAccents(str: string): string {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }

    private addPhoneContact(phoneNumber: string) {
        if (!StringUtil.isEmptyString(phoneNumber)) {
            if (this.layout.sourceContacts.mode !== DataSourceMode.ADD) {
                this.layout.sourceContacts.mode = DataSourceMode.ADD;
            }
            this.layout.sourceContacts.createBlankRow().then(contactRow => {
                contactRow.set("name", "Main");
                contactRow.set("phone", phoneNumber);
                (this.layout.layoutContactTable as TableContact).tableContacts.addRow(contactRow, {}, { display: true, addToData: true });
            })
        }
    }

    private createGoogleSearchMessagePanel(googlePlacesResult: ModelRow) {
        const code = googlePlacesResult.get("location_code");
        const layoutType = this.layout instanceof Location ? "Location" : "Customer";
        const message = `This ${layoutType} already exists as code ` + code + `.  Would you like to view the ${layoutType} details or create a duplicate?`

        CommonDialogs.showYesNo(message, `${layoutType} Already Exists`, { noButtonCaption: "Create Duplicate", noButtonProps: { color: "primary", width: 150 }, yesButtonCaption: "View", yesButtonProps: { width: 150 } }, false).then(clickedYes => {
            if (clickedYes) {
                Navigation.navigateTo(`${this.layout.layoutName}?mode=update&key=` + code);
            } else if (clickedYes === false) {
                this.createLocation(googlePlacesResult);
            } else {
                this.layout.textboxGooglePlacesSearch.clear();
            }
        });
    }
}
